import { OnDestroy, Injectable, effect, inject } from '@angular/core';
import { LoggerService } from '@core/services/infra/logger.service';;
import { Observable, from, of, combineLatest, throwError, filter, map, switchMap, take } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Document, DocumentType, ImageVariant, ImageVariants } from '@shared/definitions/document';
import { 
  Firestore,
  QueryConstraint} from '@angular/fire/firestore';
import { 
  collection, 
  collectionData, 
  doc, 
  docData, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  getDoc,
  getDocsFromServer,
  orderBy,
  limit as limitToFirst,
  startAfter,
  Timestamp
} from '@angular/fire/firestore';
import { 
  Storage, 
  StorageReference
} from '@angular/fire/storage';
import { 
  ref, 
  uploadBytesResumable, 
  getDownloadURL, 
  deleteObject
} from '@angular/fire/storage';
import { serverTimestamp } from '@firebase/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { AuthStore } from '@core/services/auth/auth.store';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { User } from '@shared/definitions/user';

@Injectable({
  providedIn: 'root'
})
export class DocumentService implements OnDestroy {
  private readonly CONTEXT = 'DocumentService';

  private readonly collectionName = 'documents';
  private readonly STORAGE_PATH = 'documents';
  private user: User | null = null;

  private readonly authStore = inject(AuthStore);

  constructor(private firestore: Firestore,
    private storage: Storage,
    private functions: Functions,
    private pathService: FirestorePathService,
    private workspaceStore: WorkspaceStore, private logger: LoggerService) {
    // Log to confirm service initialization
    this.logger.debug('DocumentService initialized', this.CONTEXT);
    this.logger.debug('Firestore instance:', this.CONTEXT, !!this.firestore);
    this.logger.debug('Storage instance:', this.CONTEXT, !!this.storage);
    
    this.initializeService();
  }

  private initializeService(): void {
    // React to auth changes via signal
    effect(() => {
      this.user = this.authStore.user();
    });
  }

  ngOnDestroy(): void {
    // Signal-based auth; no manual subscription cleanup needed.
  }

  /**
   * Get the user-scoped collection reference
   */
  private getCollection() {
    const path = this.pathService.getWorkspaceCollection(this.collectionName);
    return collection(this.firestore, path);
  }

  /**
   * Get a user-scoped document reference
   */
  private getDocument(documentId: string) {
    const path = this.pathService.getWorkspaceDocument(this.collectionName, documentId);
    return doc(this.firestore, path);
  }

  /**
   * Check if current user is authenticated
   */
  private isAuthenticated(): boolean {
    return !!this.authStore.user()?.userUID;
  }

  /**
   * Helper method to add standard timestamps to document data
   */
  private addTimestamps<T extends Record<string, unknown>>(data: T, isUpdate = false): T & {
    createdAt?: Date;
    updatedAt: Date;
  } {
    const now = new Date();
    const result: Record<string, unknown> = { ...data, updatedAt: now };

    if (!isUpdate) {
      result['createdAt'] = now;
    }

    return result as T & { createdAt?: Date; updatedAt: Date };
  }

  /**
   * Lightweight existence check for the nav "Documents" entry. The navigation
   * loader only needs a boolean (does this workspace have ANY document?), so
   * read a single doc with `limit(1)` instead of streaming the whole
   * `documents` collection on every session — one read regardless of corpus
   * size. Used by `NavigationLoaderService`.
   */
  documentsExist$(): Observable<boolean> {
    if (!this.isAuthenticated()) {
      return of(false);
    }
    const existsQuery = query(this.getCollection(), limitToFirst(1));
    return collectionData(existsQuery, { idField: 'documentUID' }).pipe(
      take(1),
      map(documents => documents.length > 0),
      catchError(() => of(false))
    );
  }

  // Get all documents with optional filtering
  getDocuments(filters: {
    entityId?: string;
    entityType?: string;
    tags?: string[];
    types?: DocumentType[];
    forceRefresh?: boolean;
    includeRelated?: boolean;
  } = {}): Observable<Document[]> {
    // this.logger.debug('Getting documents with filters:', this.CONTEXT, filters);
    
    // Check authentication
    if (!this.isAuthenticated()) {
      this.logger.error('No authenticated user found', this.CONTEXT);
      return of([]);
    }
    
    // Use the new user-scoped collection - no need for ownerUID filter
    const documentsCollection = this.getCollection();
    let documentsQuery = query(documentsCollection);
    
    // Filter by entity ID if specified, but only apply if we're not including related documents
    if (filters.entityId && !filters.includeRelated) {
      documentsQuery = query(documentsCollection, where('entityId', '==', filters.entityId));
    }
    
    // Filter by entity type if specified, but only apply if we're not including related documents
    if (filters.entityType && !filters.includeRelated) {
      const queryConstraints: QueryConstraint[] = [];
      if (filters.entityId) {
        queryConstraints.push(where('entityId', '==', filters.entityId));
      }
      queryConstraints.push(where('entityType', '==', filters.entityType));
      documentsQuery = query(documentsCollection, ...queryConstraints);
    }
    
    // Filter by document types if specified
    if (filters.types && filters.types.length > 0) {
      // Firestore doesn't support OR queries across fields without composite indexes
      // For simplicity, we'll just use the first type provided
      const queryConstraints: QueryConstraint[] = [];
      if (filters.entityId && !filters.includeRelated) {
        queryConstraints.push(where('entityId', '==', filters.entityId));
      }
      if (filters.entityType && !filters.includeRelated) {
        queryConstraints.push(where('entityType', '==', filters.entityType));
      }
      queryConstraints.push(where('type', '==', filters.types[0]));
      documentsQuery = query(documentsCollection, ...queryConstraints);
    }
    
    // Log if we're forcing refresh
    if (filters.forceRefresh) {
      this.logger.debug('Forcing refresh of document data', this.CONTEXT);
    }
    
    // `collectionData` + `take(1)` keeps the FIRST snapshot, which Firestore may serve from the
    // local cache while another listener has only part of the collection cached: a document
    // uploaded in an earlier session is then missing. `forceRefresh` reads the server instead.
    const source$: Observable<Record<string, unknown>[]> = filters.forceRefresh
      ? from(getDocsFromServer(documentsQuery)).pipe(
          map(snapshot => snapshot.docs.map(d => ({ ...d.data(), documentUID: d.id })))
        )
      : collectionData(documentsQuery, { idField: 'documentUID' }).pipe(
          // Take 1 to avoid continuous updates which aren't needed
          take(1)
        );

    return source$
      .pipe(
        map(documents => {
          // this.logger.debug(`Retrieved ${documents.length} documents`, this.CONTEXT);
          
          // Convert any Firestore Timestamps to JavaScript Date objects
          let convertedDocs = this.convertFirestoreTimestamps(documents as Record<string, unknown>[]);
          
          // If includeRelated is true and we have entityId and entityType filters,
          // filter documents to include those related to the entity
          if (filters.includeRelated && filters.entityId && filters.entityType) {
            // First let's include documents directly linked to this entity
            const directlyLinkedDocs = convertedDocs.filter(doc => 
              doc.entityId === filters.entityId && doc.entityType === filters.entityType
            );
            
            // For property view - include contract and loan documents
            if (filters.entityType === 'property') {
              const relatedDocs = convertedDocs.filter(doc => 
                // Include documents where:
                // 1. They are linked to a contract, loan, or maintenance request AND
                // 2. The contract, loan, or maintenance has propertyUID tag matching our property ID
                (doc.entityType === 'contract' || doc.entityType === 'loan' || doc.entityType === 'maintenance') && 
                ((doc.tags && filters.entityId && doc.tags.includes(filters.entityId)) || 
                 (doc.propertyUID === filters.entityId))
              );
              
              // Combine direct and related docs, removing duplicates
              convertedDocs = [...directlyLinkedDocs, ...relatedDocs];
            }
            
            // For tenant view - include contract documents
            else if (filters.entityType === 'tenant') {
              const relatedDocs = convertedDocs.filter(doc => 
                // Include documents where:
                // 1. They are linked to a contract AND
                // 2. The contract has a tag with this tenant ID
                doc.entityType === 'contract' && 
                (doc.tags && filters.entityId && doc.tags.includes(filters.entityId))
              );
              
              // Combine direct and related docs, removing duplicates
              convertedDocs = [...directlyLinkedDocs, ...relatedDocs];
            }
            
            // For contract view - include property and tenant documents
            else if (filters.entityType === 'contract') {
              // For contract view, we need to know the property and tenant IDs
              // We can extract these from the tags of the contract documents
              const contractDocs = convertedDocs.filter(doc => 
                doc.entityId === filters.entityId && doc.entityType === 'contract'
              );
              
              // Extract property and tenant IDs from tags or propertyUID
              const propertyIds: string[] = [];
              const tenantIds: string[] = [];
              
              contractDocs.forEach(doc => {
                if (doc.propertyUID) {
                  propertyIds.push(doc.propertyUID);
                }
                
                if (doc.tags) {
                  // Find tags that could be tenant IDs or property IDs
                  // This is a heuristic - in a production system we would
                  // have more explicit relationships in the data model
                  doc.tags.forEach(tag => {
                    // Assuming tenant IDs and property IDs have a specific format
                    // or prefix we could check here
                    if (tag && !propertyIds.includes(tag) && !tenantIds.includes(tag) && 
                        tag !== 'contract' && tag !== 'tenant' && tag !== 'property') {
                      tenantIds.push(tag);
                    }
                  });
                }
              });
              
              // Get documents related to these properties or tenants
              const relatedDocs = convertedDocs.filter(doc => 
                // Include documents where:
                // 1. They are linked to a property or tenant AND
                // 2. The property or tenant ID matches one we found
                ((doc.entityType === 'property' && doc.entityId && propertyIds.includes(doc.entityId)) ||
                 (doc.entityType === 'tenant' && doc.entityId && tenantIds.includes(doc.entityId)))
              );
              
              // Combine direct and related docs, removing duplicates
              convertedDocs = [...directlyLinkedDocs, ...relatedDocs];
            }
            
            // Remove duplicates by document ID
            const uniqueDocs = Array.from(
              new Map(convertedDocs.map(doc => [doc.documentUID, doc])).values()
            );
            
            this.logger.debug(`Filtered to ${uniqueDocs.length} documents after including related documents`, this.CONTEXT);
            convertedDocs = uniqueDocs;
          } else if (filters.entityId && filters.entityType) {
            // Standard filtering - only show documents directly linked to this entity
            convertedDocs = convertedDocs.filter(doc => 
              doc.entityId === filters.entityId && doc.entityType === filters.entityType
            );
          }
          
          // Filter by tags if specified
          if (filters.tags && filters.tags.length > 0) {
            convertedDocs = convertedDocs.filter(doc => 
              doc.tags && filters.tags!.some(tag => doc.tags!.includes(tag))
            );
          }
          
          // this.logger.debug(`Final filtered result: ${convertedDocs.length} documents`, this.CONTEXT);
          return convertedDocs;
        }),
        catchError(error => {
          this.logger.error('Error getting documents:', this.CONTEXT, error);
          throw error;
        })
      );
  }

  // Get a single document by ID
  /**
   * Pièces jointes d'une dépense : celles rattachées à son identifiant, plus
   * celles que `documentUIDs` liste mais qui portent encore l'identifiant
   * provisoire (« temp-… ») d'avant l'enregistrement. Sans ce complément, une
   * dépense créée avec plusieurs documents n'en montrait aucun.
   */
  getExpenseDocuments(expenseUID: string, documentUIDs: readonly string[] = []): Observable<Document[]> {
    return this.getDocuments({ entityId: expenseUID, entityType: 'expense' }).pipe(
      take(1),
      switchMap(linked => {
        const known = new Set(linked.map(d => d.documentUID));
        const missing = documentUIDs.filter(id => !!id && !known.has(id));
        if (missing.length === 0) return of(linked);
        return combineLatest(
          missing.map(id => this.getDocumentById(id).pipe(take(1), catchError(() => of(undefined))))
        ).pipe(map(extra => [...linked, ...extra.filter((d): d is Document => !!d)]));
      })
    );
  }

  getDocumentById(documentId: string): Observable<Document | undefined> {
    this.logger.debug(`Getting document with ID: ${documentId}`, this.CONTEXT);
    
    if (!this.isAuthenticated()) {
      this.logger.error('No authenticated user found', this.CONTEXT);
      return of(undefined);
    }
    
    const documentRef = this.getDocument(documentId);
    return docData(documentRef, { idField: 'documentUID' }).pipe(
      map(document => {
        if (document) {
          // Convert any Firestore Timestamps to JavaScript Date objects
          const convertedDoc = this.convertFirestoreTimestamps([document as Record<string, unknown>])[0];
          this.logger.debug(`Retrieved document: ${convertedDoc.title}`, this.CONTEXT);
          return convertedDoc;
        } else {
          this.logger.debug(`No document found with ID: ${documentId}`, this.CONTEXT);
          return undefined;
        }
      }),
      catchError(error => {
        this.logger.error('Error getting document by ID:', this.CONTEXT, error);
        throw error;
      })
    );
  }

  /**
   * Resolve the related property/tenant of a contract/loan and fold them into
   * the document's tags + propertyUID BEFORE the doc is written, so the file
   * shows up in the Property/Tenant Documents tabs.
   *
   * This used to be fire-and-forget AND broken twice over: it read the contract/
   * loan from non-existent TOP-LEVEL collections (they live under
   * workspaces/{id}/...), and gated its write on a documentUID that did not exist
   * yet (enrichment ran before addDoc). Now it resolves via the workspace-scoped
   * path and is awaited, so the initial write already carries the tags.
   */
  private async addRelatedEntityTags(
    metadata: { entityType?: string; entityId?: string; tags: string[]; documentUID?: string; propertyUID?: string; [key: string]: unknown }
  ): Promise<typeof metadata> {
    const updatedMetadata = { ...metadata, tags: [...(metadata.tags ?? [])] };

    try {
      if (metadata.entityType === 'contract' && metadata.entityId) {
        const ref = doc(this.firestore, this.pathService.getWorkspaceDocument('contracts', metadata.entityId));
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const c = snap.data();
          const propertyUID = c['propertyUID'] as string | undefined;
          const tenantUID = (c['tenantUID'] ?? c['primaryTenantUID']) as string | undefined;
          if (propertyUID) {
            if (!updatedMetadata.tags.includes(propertyUID)) updatedMetadata.tags.push(propertyUID);
            updatedMetadata.propertyUID = propertyUID;
          }
          if (tenantUID && !updatedMetadata.tags.includes(tenantUID)) updatedMetadata.tags.push(tenantUID);
        }
      } else if (metadata.entityType === 'loan' && metadata.entityId) {
        const ref = doc(this.firestore, this.pathService.getWorkspaceDocument('loans', metadata.entityId));
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const l = snap.data();
          const propertyUID = l['propertyUID'] as string | undefined;
          if (propertyUID) {
            if (!updatedMetadata.tags.includes(propertyUID)) updatedMetadata.tags.push(propertyUID);
            updatedMetadata.propertyUID = propertyUID;
          }
        }
      }
    } catch (error) {
      // Best-effort: never block the upload on a failed tag enrichment.
      this.logger.error('Error enriching related-entity tags:', this.CONTEXT, error);
    }

    return updatedMetadata;
  }

  // Helper method to remove undefined values from object (Firestore doesn't accept undefined)
  private removeUndefinedValues<T extends Record<string, unknown>>(obj: T): Partial<T> {
    const cleaned: Partial<T> = {};
    Object.keys(obj).forEach(key => {
      if (obj[key] !== undefined) {
        cleaned[key as keyof T] = obj[key] as T[keyof T];
      }
    });
    return cleaned;
  }

  // Enhanced upload method that emits progress updates
  uploadDocumentWithProgress(
    file: File,
    metadata: Omit<Document, 'documentUID' | 'fileUrl' | 'fileName' | 'fileSize' | 'fileType' | 'uploadDate'> & { entityId: string, entityType: string }
  ): Observable<{ type: 'progress', progress: number } | { type: 'complete', document: Document }> {
    this.logger.debug('Starting document upload process with progress tracking', this.CONTEXT);
    this.logger.debug('File size: ' + file.size + ' bytes', this.CONTEXT);
    
    if (!this.isAuthenticated()) {
      this.logger.error('No authenticated user found', this.CONTEXT);
      return throwError(() => new Error('User not authenticated'));
    }

    // Get current user
    const currentUser = this.authStore.user();
    
    // Get current workspace ID
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    this.logger.debug('[DocumentService] Current workspace ID:', this.CONTEXT, workspaceId);
    this.logger.debug('[DocumentService] Current user ID:', this.CONTEXT, currentUser?.userUID);
    
    if (!workspaceId) {
      this.logger.error('[DocumentService] ❌ No workspace selected!', this.CONTEXT);
      this.logger.error('[DocumentService] User:', this.CONTEXT, currentUser);
      return throwError(() => new Error('No workspace selected. Please ensure you are logged in and a workspace is active.'));
    }
    
    this.logger.debug('[DocumentService] ✅ Workspace ID confirmed:', this.CONTEXT, workspaceId);
    
    // Create a unique filename to avoid conflicts
    const timestamp = Date.now();
    const _fileExtension = file.name.split('.').pop();
    const uniqueFileName = `${timestamp}_${file.name}`;
    
    // Create storage reference using workspace-scoped path structure (v2.0)
    // Pattern: workspaces/{workspaceId}/documents/{entityType}/{entityId}/{fileName}
    // This provides workspace-based access control
    const storagePath = `workspaces/${workspaceId}/documents/${metadata.entityType}/${metadata.entityId}/${uniqueFileName}`;
    this.logger.debug('[DocumentService] 📁 Storage path:', this.CONTEXT, storagePath);
    this.logger.debug('[DocumentService] 📝 Metadata:', this.CONTEXT, {
      entityType: metadata.entityType,
      entityId: metadata.entityId,
      title: metadata.title
    });
    const storageRef = ref(this.storage, storagePath);
    
    // Upload file
    const uploadTask = uploadBytesResumable(storageRef, file);
    
    return new Observable<{ type: 'progress', progress: number } | { type: 'complete', document: Document }>(observer => {
      let lastEmittedProgress = 0;
      
      uploadTask.on('state_changed',
        (snapshot) => {
          // Progress monitoring - emit progress updates
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          const roundedProgress = Math.round(progress);
          
          this.logger.debug(`Upload progress: ${progress}% (${snapshot.bytesTransferred}/${snapshot.totalBytes} bytes)`, this.CONTEXT);
          
          // Only emit if progress has increased by at least 1% or if it's significant milestones
          if (roundedProgress > lastEmittedProgress || roundedProgress === 0 || roundedProgress === 100) {
            lastEmittedProgress = roundedProgress;
            this.logger.debug(`Emitting progress: ${roundedProgress}%`, this.CONTEXT);
            observer.next({ type: 'progress', progress: roundedProgress });
          }
        },
        (error) => {
          this.logger.error('Upload failed:', this.CONTEXT, error);
          observer.error(error);
        },
        async () => {
          try {
            // Upload completed successfully - emit final progress before processing
            this.logger.debug('File upload completed', this.CONTEXT);
            observer.next({ type: 'progress', progress: 100 });
            
            // Give a small delay to ensure UI shows 100% before processing
            await new Promise(resolve => setTimeout(resolve, 100));
            
            // Get download URL
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            this.logger.debug('Download URL obtained:', this.CONTEXT, downloadURL);
            
            // Prepare document metadata
            const documentData = {
              ...metadata,
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              fileUrl: downloadURL,
              uploadDate: new Date(),
              // Store the storage path for easier deletion later
              storagePath: `workspaces/${workspaceId}/documents/${metadata.entityType}/${metadata.entityId}/${uniqueFileName}`,
              // ownerUID removed - implicit in workspace path
              isArchived: false,
              tags: metadata.tags || []
            };

            // Add related entity tags (awaited so the tags are in the initial write).
            const updatedMetadata = await this.addRelatedEntityTags(documentData);
            
            // Use addTimestamps helper
            const finalDocumentData = this.addTimestamps(updatedMetadata, false);
            
            try {
              // Save document metadata to Firestore using user-scoped collection
              this.logger.debug('Saving document metadata to Firestore', this.CONTEXT);
              const documentsCollection = this.getCollection();
              
              // Remove undefined values before saving to Firestore
              const cleanedData = this.removeUndefinedValues(finalDocumentData);
              this.logger.debug('Cleaned document data:', this.CONTEXT, cleanedData);
              
              const docRef = await addDoc(documentsCollection, cleanedData);
              this.logger.debug('Document metadata saved with ID:', this.CONTEXT, docRef.id);
              
              // Return the complete document
              const completeDocument: Document = {
                ...finalDocumentData,
                documentUID: docRef.id
              } as unknown as Document;
              
              observer.next({ type: 'complete', document: completeDocument });
              observer.complete();
            } catch (firestoreError) {
              this.logger.error('Error saving document metadata:', this.CONTEXT, firestoreError);
              observer.error(firestoreError);
            }
          } catch (error) {
            this.logger.error('Error getting download URL:', this.CONTEXT, error);
            observer.error(error);
          }
        }
      );
    });
  }

  uploadDocument(
    file: File, 
    metadata: Omit<Document, 'documentUID' | 'fileUrl' | 'fileName' | 'fileSize' | 'fileType' | 'uploadDate'> & { entityId: string, entityType: string }
  ): Observable<Document> {
    // Use the progress version but only return the document
    return this.uploadDocumentWithProgress(file, metadata).pipe(
      filter(result => result.type === 'complete'),
      map(result => (result as { type: 'complete', document: Document }).document)
    );
  }

  /**
   * Copy a single Storage object to a new path by streaming the bytes through
   * the client, returning the new download URL. Mirrors the blob-copy used by
   * `restoreDocument`.
   */
  private async copyStorageFile(sourceUrl: string, newStoragePath: string): Promise<string> {
    const response = await fetch(sourceUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch source file (${response.status})`);
    }
    const blob = await response.blob();
    const newRef = ref(this.storage, newStoragePath);
    await uploadBytesResumable(newRef, blob);
    return getDownloadURL(newRef);
  }

  /**
   * Duplicate an existing document onto another entity (e.g. when copying a
   * property). The underlying Storage file(s) are copied to fresh, independent
   * paths — the new record never shares blobs with the source, so deleting the
   * original cannot break the copy. Photo `variants` are each re-uploaded and
   * the variants map rebuilt with the new URLs/paths. `tags` (which encode
   * primary/category/sortOrder for photos) are preserved, minus any tag that
   * referenced the source entity. The backend `onDocumentCreatedTrigger`
   * regenerates the document summary from the new record.
   */
  async cloneDocumentToEntity(
    source: Document,
    target: { entityId: string; entityType: string; propertyUID: string }
  ): Promise<Document> {
    const workspaceId = this.workspaceStore.currentWorkspaceId();
    if (!workspaceId) {
      throw new Error('No workspace selected');
    }

    const timestamp = Date.now();
    const safeName = source.fileName || 'file';
    const basePath = `workspaces/${workspaceId}/documents/${target.entityType}/${target.entityId}`;

    // Photo variants: copy every variant blob to a new path.
    let newVariants: ImageVariants | undefined;
    if (source.variants && Object.keys(source.variants).length > 0) {
      const rebuilt: Record<string, ImageVariant> = {};
      for (const [variantName, variant] of Object.entries(source.variants)) {
        if (!variant?.url) {
          continue;
        }
        const variantPath = `${basePath}/${timestamp}_${variantName}_${safeName}`;
        const url = await this.copyStorageFile(variant.url, variantPath);
        rebuilt[variantName] = {
          url,
          storagePath: variantPath,
          width: variant.width,
          height: variant.height,
          size: variant.size
        };
      }
      newVariants = rebuilt as ImageVariants;
    }

    // Main file: prefer the original/large variant; otherwise copy the single file.
    let newFileUrl = '';
    let newStoragePath: string | undefined;
    if (newVariants && (newVariants.original || newVariants.large)) {
      const main = newVariants.original ?? newVariants.large;
      newFileUrl = main?.url ?? '';
      newStoragePath = main?.storagePath;
    } else if (source.fileUrl) {
      newStoragePath = `${basePath}/${timestamp}_${safeName}`;
      newFileUrl = await this.copyStorageFile(source.fileUrl, newStoragePath);
    }

    const newRecord: Record<string, unknown> = {
      type: source.type,
      title: source.title,
      description: source.description,
      entityId: target.entityId,
      entityType: target.entityType,
      propertyUID: target.propertyUID,
      fileName: source.fileName,
      fileSize: source.fileSize,
      fileType: source.fileType,
      fileUrl: newFileUrl,
      storagePath: newStoragePath,
      uploadDate: new Date(),
      isArchived: source.isArchived ?? false,
      tags: (source.tags ?? []).filter(tag => tag !== source.propertyUID && tag !== source.entityId),
      variants: newVariants
    };

    const finalData = this.addTimestamps(newRecord, false);
    const cleaned = this.removeUndefinedValues(finalData);
    const docRef = await addDoc(this.getCollection(), cleaned);
    return new Document({ ...(cleaned as Partial<Document>), documentUID: docRef.id });
  }

  // Update document metadata (not the file itself)
  updateDocumentMetadata(documentId: string, metadata: Partial<Document>): Observable<void> {
    this.logger.debug(`Updating document metadata for ID: ${documentId}`, this.CONTEXT);
    
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }
    
    const documentRef = this.getDocument(documentId);
    const updateData = this.addTimestamps(metadata, true);
    // Remove undefined values before updating Firestore
    const cleanedUpdateData = this.removeUndefinedValues(updateData);
    return from(updateDoc(documentRef, cleanedUpdateData));
  }

  // Helper method to get storage reference from URL or path
  private getStorageRefFromDocument(document: Document): StorageReference {
    // Check if we have the storage path directly
    if (document.storagePath) {
      this.logger.debug('Using stored path for deletion:', this.CONTEXT, document.storagePath);
      return ref(this.storage, document.storagePath);
    }
    
    // Try to extract the path from the URL
    try {
      // Extract the path from the download URL
      // Example URL: https://firebasestorage.googleapis.com/v0/b/bucket-name/o/documents%2Fproperty%2F123%2Ffile.pdf?alt=media&token=abc123
      const url = new URL(document.fileUrl);
      const fullPath = decodeURIComponent(url.pathname.split('/o/')[1].split('?')[0]);
      this.logger.debug('Extracted path from URL:', this.CONTEXT, fullPath);
      return ref(this.storage, fullPath);
    } catch (error) {
      this.logger.error('Failed to parse storage URL, using URL directly', this.CONTEXT, error);
      // Fallback to trying the URL directly
      return ref(this.storage, document.fileUrl);
    }
  }

  // Delete a document and its file
  deleteDocument(documentId: string): Observable<void> {
    this.logger.debug(`Deleting document with ID: ${documentId}`, this.CONTEXT);

    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    // First get the document to retrieve file information
    // Use take(1) to ensure we only get the first emission and complete
    return this.getDocumentById(documentId).pipe(
      take(1),
      switchMap(document => {
        if (!document) {
          // Document not found in Firestore - try to delete just the metadata reference
          // This can happen if the document was just uploaded but not yet fully synced
          this.logger.warn(`Document ${documentId} not found in Firestore, attempting direct deletion`, this.CONTEXT);
          const documentRef = this.getDocument(documentId);
          return from(deleteDoc(documentRef)).pipe(
            map(() => {
              this.logger.debug('Document metadata deleted (document was not found initially)', this.CONTEXT);
              return;
            }),
            catchError(deleteError => {
              this.logger.error('Failed to delete document reference:', this.CONTEXT, deleteError);
              throw new Error('Document not found');
            })
          );
        }

        // Delete the file from storage
        this.logger.debug('Deleting file from storage', this.CONTEXT);
        const storageRef = this.getStorageRefFromDocument(document);
        const deleteFile$ = from(deleteObject(storageRef)).pipe(
          catchError(err => {
            // If file doesn't exist in storage, continue with metadata deletion
            this.logger.warn('File may not exist in storage, continuing with metadata deletion:', this.CONTEXT, err);
            return of(void 0);
          })
        );

        // Delete the metadata from Firestore using user-scoped document reference
        this.logger.debug('Deleting document metadata from Firestore', this.CONTEXT);
        const documentRef = this.getDocument(documentId);
        const deleteMetadata$ = from(deleteDoc(documentRef));

        // Execute both deletions
        return combineLatest([deleteFile$, deleteMetadata$]).pipe(
          map(() => {
            this.logger.debug('Document and file deleted successfully', this.CONTEXT);
            return;
          })
        );
      }),
      catchError(error => {
        this.logger.error('Error deleting document:', this.CONTEXT, error);
        throw error;
      })
    );
  }

  // Archive/unarchive a document
  toggleArchiveStatus(documentId: string, isArchived: boolean): Observable<void> {
    this.logger.debug(`Toggling archive status for document ID: ${documentId} to ${isArchived}`, this.CONTEXT);
    
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }
    
    const documentRef = this.getDocument(documentId);
    const updateData = this.addTimestamps({ isArchived }, true);
    // Remove undefined values before updating Firestore
    const cleanedUpdateData = this.removeUndefinedValues(updateData);
    return from(updateDoc(documentRef, cleanedUpdateData));
  }

  // Get document download URL
  getDownloadUrl(documentId: string): Observable<string | null> {
    this.logger.debug(`Getting download URL for document ID: ${documentId}`, this.CONTEXT);
    return this.getDocumentById(documentId).pipe(
      take(1),
      map(document => document?.fileUrl || null),
      catchError(() => of(null))
    );
  }

  // Get all user's documents globally (for document browser section)
  getAllUserDocuments(options: {
    archived?: boolean,
    type?: DocumentType,
    searchTerm?: string
  } = {}): Observable<Document[]> {
    this.logger.debug('Getting all user documents with options:', this.CONTEXT, options);
    
    // Check authentication
    if (!this.isAuthenticated()) {
      this.logger.error('No authenticated user found', this.CONTEXT);
      return of([]);
    }
    
    // Use the new user-scoped collection - no need for ownerUID filter
    const documentsCollection = this.getCollection();
    
    // Apply filters
    const queryConstraints: QueryConstraint[] = [];
    
    // Filter for archived state if specified
    if (options.archived !== undefined) {
      queryConstraints.push(where('isArchived', '==', options.archived));
    }
    
    // Filter by type if specified
    if (options.type) {
      queryConstraints.push(where('type', '==', options.type));
    }
    
    const documentsQuery = queryConstraints.length > 0 
      ? query(documentsCollection, ...queryConstraints)
      : query(documentsCollection);
    
    return collectionData(documentsQuery, { idField: 'documentUID' })
      .pipe(
        take(1),
        map(documents => {
          this.logger.debug(`Retrieved ${documents.length} user documents`, this.CONTEXT);
          
          // Convert any Firestore Timestamps to JavaScript Date objects
          const convertedDocs = this.convertFirestoreTimestamps(documents as Record<string, unknown>[]);
          
          // Apply search term filtering in memory since Firestore doesn't support full-text search
          if (options.searchTerm && options.searchTerm.trim() !== '') {
            const searchLower = options.searchTerm.toLowerCase();
            return convertedDocs.filter(doc => 
              doc.title.toLowerCase().includes(searchLower) || 
              (doc.description && doc.description.toLowerCase().includes(searchLower)) ||
              doc.fileName.toLowerCase().includes(searchLower)
            );
          }
          
          return convertedDocs;
        }),
        catchError(error => {
          this.logger.error('Error getting all user documents:', this.CONTEXT, error);
          throw error;
        })
      );
  }

  private convertFirestoreTimestamps(documents: Record<string, unknown>[]): Document[] {
    return documents.map(doc => {
      // Convert uploadDate from Firestore Timestamp to Date if needed
      const uploadDate = doc['uploadDate'];
      if (uploadDate && typeof (uploadDate as { toDate?: unknown }).toDate === 'function') {
        doc['uploadDate'] = (uploadDate as { toDate: () => Date }).toDate();
      }

      // Convert expirationDate from Firestore Timestamp to Date if needed
      const expirationDate = doc['expirationDate'];
      if (expirationDate && typeof (expirationDate as { toDate?: unknown }).toDate === 'function') {
        doc['expirationDate'] = (expirationDate as { toDate: () => Date }).toDate();
      }

      return doc as unknown as Document;
    });
  }

  // Update a document reference with new metadata
  updateDocumentReference(documentId: string, updates: Partial<Document>): Observable<void> {
    this.logger.debug('Updating document reference:', this.CONTEXT, { documentId, updates });
    
    if (!documentId) {
      this.logger.error('No document ID provided for update', this.CONTEXT);
      return of(null).pipe(
        switchMap(() => {
          throw new Error('Document ID is required');
        })
      );
    }
    
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }
    
    try {
      const docRef = this.getDocument(documentId);
      
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };
      
      return from(updateDoc(docRef, updateData)).pipe(
        map(() => {
          this.logger.debug(`Document ${documentId} updated successfully`, this.CONTEXT);
          return;
        }),
        catchError(error => {
          this.logger.error('Error updating document reference:', this.CONTEXT, error);
          throw error;
        })
      );
    } catch (error) {
      this.logger.error('Error updating document reference:', this.CONTEXT, error);
      return of(null).pipe(
        switchMap(() => {
          throw error;
        })
      );
    }
  }

  // Helper method to enrich documents with entity names
  private enrichDocumentsWithEntityNames(documents: Document[]): Document[] {
    // Group documents by entity type and ID to minimize Firestore reads
    const propertyDocs = documents.filter(doc => 
      doc.entityType === 'property' || doc.propertyUID
    );
    
    const tenantDocs = documents.filter(doc => 
      doc.entityType === 'tenant' || (doc.tags && doc.tags.some(tag => tag.startsWith('tenant-')))
    );
    
    const loanDocs = documents.filter(doc => 
      doc.entityType === 'loan'
    );
    
    const contractDocs = documents.filter(doc => 
      doc.entityType === 'contract'
    );
    
    // Collect unique IDs
    const propertyIds = new Set<string>();
    const tenantIds = new Set<string>();
    const loanIds = new Set<string>();
    const contractIds = new Set<string>();
    
    // Collect property IDs
    propertyDocs.forEach(doc => {
      if (doc.entityType === 'property' && doc.entityId) {
        propertyIds.add(doc.entityId);
      } else if (doc.propertyUID) {
        propertyIds.add(doc.propertyUID);
      }
    });
    
    // Collect tenant IDs
    tenantDocs.forEach(doc => {
      if (doc.entityType === 'tenant' && doc.entityId) {
        tenantIds.add(doc.entityId);
      } else if (doc.tags) {
        doc.tags.forEach(tag => {
          if (tag.startsWith('tenant-')) {
            tenantIds.add(tag.substring(7)); // Remove 'tenant-' prefix
          }
        });
      }
    });
    
    // Collect loan IDs
    loanDocs.forEach(doc => {
      if (doc.entityType === 'loan' && doc.entityId) {
        loanIds.add(doc.entityId);
      }
    });
    
    // Collect contract IDs
    contractDocs.forEach(doc => {
      if (doc.entityType === 'contract' && doc.entityId) {
        contractIds.add(doc.entityId);
      }
    });
    
    // Create a Map to store entity names by ID
    const propertyNames = new Map<string, string>();
    const tenantNames = new Map<string, string>();
    const _loanNames = new Map<string, string>();
    const _contractNames = new Map<string, string>();
    
    // Function to fetch properties from Firestore
    const _fetchPropertyNames = async () => {
      for (const propertyId of propertyIds) {
        try {
          const propertyRef = doc(this.firestore, 'properties', propertyId);
          const propertyDoc = await getDoc(propertyRef);
          
          if (propertyDoc.exists()) {
            const data = propertyDoc.data();
            if (data && data['name']) {
              propertyNames.set(propertyId, data['name']);
            }
          }
        } catch (error) {
          this.logger.warn(`Error fetching property ${propertyId}:`, this.CONTEXT, error);
        }
      }
    };
    
    // Function to fetch tenants from Firestore
    const _fetchTenantNames = async () => {
      for (const tenantId of tenantIds) {
        try {
          const tenantRef = doc(this.firestore, 'tenants', tenantId);
          const tenantDoc = await getDoc(tenantRef);
          
          if (tenantDoc.exists()) {
            const data = tenantDoc.data();
            if (data) {
              let name = '';
              if (data['tenantName']) {
                name = data['tenantName'];
              } else if (data['companyName']) {
                name = data['companyName'];
              } else if (data['contacts'] && data['contacts'][0]) {
                const contact = data['contacts'][0];
                name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
              }
              
              if (name) {
                tenantNames.set(tenantId, name);
              }
            }
          }
        } catch (error) {
          this.logger.warn(`Error fetching tenant ${tenantId}:`, this.CONTEXT, error);
        }
      }
    };
    
    // For now, return documents without enrichment to avoid async complexity
    // In a production system, you might want to implement proper async enrichment
    return documents;
  }

  // =============================================================================
  // SOFT DELETE METHODS
  // =============================================================================

  /**
   * Soft delete a document (moves to trash with 30-day recovery period)
   * Instead of permanently deleting, marks as deleted and moves file to trash
   */
  softDeleteDocument(documentId: string): Observable<void> {
    this.logger.debug(`Soft deleting document ${documentId}`, this.CONTEXT);
    
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    const documentRef = this.getDocument(documentId);
    const now = new Date();
    const thirtyDaysLater = new Date(now);
    thirtyDaysLater.setDate(thirtyDaysLater.getDate() + 30);
    
    const updateData = {
      isDeleted: true,
      deletedAt: now,
      deletedBy: this.user?.userUID || '',
      permanentDeletionDate: thirtyDaysLater
    };
    
    return from(updateDoc(documentRef, updateData));
  }

  /**
   * Restore a document from trash
   * Handles both Firestore metadata and storage file restoration
   */
  restoreDocument(documentId: string): Observable<void> {
    this.logger.debug(`Restoring document ${documentId}`, this.CONTEXT);
    
    if (!this.isAuthenticated()) {
      return throwError(() => new Error('User not authenticated'));
    }

    // First get the document to check if file needs to be restored
    return this.getDocumentById(documentId).pipe(
      take(1),
      switchMap(document => {
        if (!document) {
          throw new Error('Document not found');
        }

        const documentRef = this.getDocument(documentId);
        const updateData: Record<string, unknown> = {
          isDeleted: false,
          restoredAt: new Date(),
          deletedAt: null,
          deletedBy: null,
          permanentDeletionDate: null
        };

        // If the file was moved to to_be_deleted folder, restore it
        if (document.storagePath && document.storagePath.includes('/to_be_deleted/') && document.originalStoragePath) {
          this.logger.debug(`Restoring storage file from ${document.storagePath} to ${document.originalStoragePath}`, this.CONTEXT);
          
          const deletedFileRef = ref(this.storage, document.storagePath);
          const originalFileRef = ref(this.storage, document.originalStoragePath);
          
          // Move file back to original location
          return from(
            fetch(document.fileUrl)
              .then(response => response.blob())
              .then(blob => {
                const uploadTask = uploadBytesResumable(originalFileRef, blob);
                return uploadTask.then(snapshot => snapshot);
              })
              .then(() => getDownloadURL(originalFileRef))
              .then(newUrl => {
                // Delete from to_be_deleted folder
                return deleteObject(deletedFileRef)
                  .then(() => {
                    // Update metadata with restored path
                    updateData['storagePath'] = document.originalStoragePath;
                    updateData['fileUrl'] = newUrl;
                    updateData['originalStoragePath'] = null;
                    
                    this.logger.debug(`File restored to ${document.originalStoragePath}`, this.CONTEXT);
                    return updateDoc(documentRef, updateData);
                  });
              })
              .catch(error => {
                this.logger.error('Error restoring file:', this.CONTEXT, error);
                // Even if file restoration fails, update metadata
                return updateDoc(documentRef, updateData);
              })
          ).pipe(
            map(() => void 0)
          );
        } else {
          // No file to restore, just update metadata
          this.logger.debug('No file restoration needed, this.CONTEXT, updating metadata only');
          return from(updateDoc(documentRef, updateData)).pipe(
            map(() => void 0)
          );
        }
      }),
      catchError(error => {
        this.logger.error(`Error restoring document ${documentId}:`, this.CONTEXT, error);
        return throwError(() => error);
      })
    );
  }

  // =============================================================================
  // EXPORT METHODS
  // =============================================================================

  /**
   * Export all documents for a specific entity as a ZIP file
   * Returns a download URL that expires in 1 hour
   */
  exportEntityDocuments(entityType: string, entityId: string): Observable<string> {
    this.logger.debug(`Exporting documents for ${entityType} ${entityId}`, this.CONTEXT);
    
    const exportFunction = httpsCallable(this.functions, 'exportEntityDocuments');
    
    // Get current workspace ID
    const workspaceId = this.workspaceStore.requireWorkspaceId();
    
    return from(exportFunction({ workspaceId, entityType, entityId })).pipe(
      map(result => {
        const data = result.data as { downloadUrl: string };
        this.logger.debug('Export successful:', this.CONTEXT, data);
        return data.downloadUrl;
      }),
      catchError(error => {
        this.logger.error('Error exporting documents:', this.CONTEXT, error);
        throw error;
      })
    );
  }

  /**
   * Export all documents for the current workspace
   * Optionally filter by entity type, date range, etc.
   */
  exportAllDocuments(filters?: {
    entityType?: string;
    dateFrom?: string;
    dateTo?: string;
    includeDeleted?: boolean;
  }): Observable<string> {
    this.logger.debug('Exporting all documents with filters:', this.CONTEXT, filters);
    
    const exportFunction = httpsCallable(this.functions, 'exportAllUserDocuments');
    
    // Get current workspace ID
    const workspaceId = this.workspaceStore.requireWorkspaceId();
    
    return from(exportFunction({ workspaceId, ...filters })).pipe(
      map(result => {
        const data = result.data as { downloadUrl: string };
        this.logger.debug('Export successful:', this.CONTEXT, data);
        return data.downloadUrl;
      }),
      catchError(error => {
        this.logger.error('Error exporting all documents:', this.CONTEXT, error);
        throw error;
      })
    );
  }

  /**
   * Trigger browser download from URL
   */
  downloadFromUrl(url: string, filename: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Get document summaries for list views
   * Uses lightweight summary collection instead of full documents
   * Provides pagination and filtering for better performance
   */
  getDocumentSummaries(filters: {
    entityId?: string;
    entityType?: string;
    type?: DocumentType;
    searchTerm?: string;
    dateFrom?: Date;
    dateTo?: Date;
    limit?: number;
    startAfter?: unknown;
  } = {}): Observable<{ summaries: Document[], hasMore: boolean, lastDoc: unknown }> {
    
    if (!this.isAuthenticated()) {
      this.logger.error('No authenticated user found', this.CONTEXT);
      return of({ summaries: [], hasMore: false, lastDoc: null });
    }
    
    const currentUser = this.authStore.user();
    const summariesCollection = collection(this.firestore, `users/${currentUser?.userUID ?? ''}/document_summaries`);
    
    const queryConstraints: QueryConstraint[] = [];
    
    // Filter by entity
    if (filters.entityId) {
      queryConstraints.push(where('entityId', '==', filters.entityId));
    }
    if (filters.entityType) {
      queryConstraints.push(where('entityType', '==', filters.entityType));
    }
    
    // Filter by type
    if (filters.type) {
      queryConstraints.push(where('type', '==', filters.type));
    }
    
    // Date range filters
    if (filters.dateFrom) {
      queryConstraints.push(where('uploadDate', '>=', Timestamp.fromDate(filters.dateFrom)));
    }
    if (filters.dateTo) {
      queryConstraints.push(where('uploadDate', '<=', Timestamp.fromDate(filters.dateTo)));
    }
    
    // Order by upload date descending (latest first)
    queryConstraints.push(orderBy('uploadDate', 'desc'));
    
    // Pagination
    const limit = filters.limit || 50;
    queryConstraints.push(limitToFirst(limit + 1)); // Fetch one extra to check hasMore
    
    if (filters.startAfter) {
      queryConstraints.push(startAfter(filters.startAfter));
    }
    
    const summariesQuery = query(summariesCollection, ...queryConstraints);
    
    return collectionData(summariesQuery, { idField: 'documentUID' }).pipe(
      take(1),
      map(results => {
        const hasMore = results.length > limit;
        const summaries = hasMore ? results.slice(0, limit) : results;
        const lastDoc = summaries.length > 0 ? summaries[summaries.length - 1] : null;

        // Convert Firestore timestamps
        let filtered = this.convertFirestoreTimestamps(summaries as Record<string, unknown>[]);

        // Client-side search filter (for search term on title/filename/entity name)
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          filtered = filtered.filter(s =>
            s.title?.toLowerCase().includes(searchLower) ||
            s.fileName?.toLowerCase().includes(searchLower) ||
            (s as { entityName?: string }).entityName?.toLowerCase().includes(searchLower)
          );
        }

        // Filter out deleted items
        filtered = filtered.filter(s => !(s as { isDeleted?: boolean }).isDeleted);
        
        this.logger.debug(`Loaded ${filtered.length} document summaries`, this.CONTEXT);
        
        return {
          summaries: filtered,
          hasMore,
          lastDoc
        };
      }),
      catchError(error => {
        this.logger.error('Error getting document summaries:', this.CONTEXT, error);
        throw error;
      })
    );
  }

  /**
   * Rename a document
   * Updates both the full document and sharded ledger
   */
  renameDocument(documentId: string, newTitle: string): Observable<void> {
    this.logger.debug(`Renaming document ${documentId} to "${newTitle}"`, this.CONTEXT);
    
    // Use the new sharded ledger version
    const renameFunc = httpsCallable(this.functions, 'renameDocumentSharded');
    
    // Get current workspace ID
    const workspaceId = this.workspaceStore.requireWorkspaceId();
    
    return from(renameFunc({ workspaceId, documentId, newTitle })).pipe(
      map(() => {
        this.logger.debug(`Successfully renamed document ${documentId}`, this.CONTEXT);
        return void 0;
      }),
      catchError(error => {
        this.logger.error('Error renaming document:', this.CONTEXT, error);
        throw error;
      })
    );
  }
}